import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const FeaturedArticle = ({ article }) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="relative mb-16"
    >
      <div className="text-center mb-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4"
        >
          <Icon name="Star" size={16} />
          <span>Featured Article</span>
        </motion.div>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          Editor's Pick
        </h2>
        <p className="text-muted-foreground">
          Our most impactful content, handpicked for you
        </p>
      </div>
      <motion.article
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="relative bg-card border border-border rounded-3xl overflow-hidden shadow-3d hover:shadow-accent transition-all duration-500 transform-3d"
      >
        {/* 3D Background Effect */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-success/5 opacity-0 group-hover:opacity-100"
          animate={{
            opacity: isHovered ? 1 : 0,
          }}
          transition={{ duration: 0.5 }}
        />

        <div className="grid lg:grid-cols-2 gap-0">
          {/* Image Section */}
          <div className="relative h-64 lg:h-auto overflow-hidden">
            <Image
              src={article?.image}
              alt={article?.title}
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
            />
            
            {/* Overlay Gradient */}
            <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-background/80 via-transparent to-transparent" />
            
            {/* Floating Elements */}
            <motion.div
              className="absolute top-6 left-6"
              animate={{
                y: isHovered ? [0, -5, 0] : 0,
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <div className="flex items-center space-x-2 bg-background/90 backdrop-blur-sm text-accent px-4 py-2 rounded-full text-sm font-medium shadow-3d">
                <Icon name={article?.category?.icon} size={16} />
                <span>{article?.category?.name}</span>
              </div>
            </motion.div>

            {/* 3D Code Preview */}
            <motion.div
              className="absolute bottom-6 right-6 opacity-80"
              animate={{
                rotate: isHovered ? [0, 5, -5, 0] : 0,
                scale: isHovered ? [1, 1.05, 1] : 1,
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <div className="bg-card/90 backdrop-blur-sm border border-border rounded-xl p-4 shadow-3d">
                <code className="text-accent text-sm font-mono block">
                  {article?.codeSnippet}
                </code>
              </div>
            </motion.div>
          </div>

          {/* Content Section */}
          <div className="p-8 lg:p-12 flex flex-col justify-center">
            {/* Author & Meta */}
            <div className="flex items-center space-x-4 mb-6">
              <Image
                src={article?.author?.avatar}
                alt={article?.author?.name}
                className="w-12 h-12 rounded-full object-cover border-2 border-accent/20"
              />
              <div>
                <div className="font-semibold text-foreground">
                  {article?.author?.name}
                </div>
                <div className="text-sm text-muted-foreground">
                  {article?.author?.role} • {formatDate(article?.publishedAt)}
                </div>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-4 leading-tight">
              {article?.title}
            </h3>

            {/* Excerpt */}
            <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
              {article?.excerpt}
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {article?.tags?.slice(0, 4)?.map((tag, index) => (
                <motion.span
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="px-3 py-1.5 bg-muted/50 text-muted-foreground text-sm rounded-lg hover:bg-accent/10 hover:text-accent transition-all duration-200"
                >
                  #{tag}
                </motion.span>
              ))}
            </div>

            {/* Stats & CTA */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={16} />
                  <span>{article?.readTime} min read</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Eye" size={16} />
                  <span>{article?.views?.toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Heart" size={16} />
                  <span>{article?.likes}</span>
                </div>
              </div>

              <Link
                to={`/blog/${article?.slug}`}
                className="group flex items-center space-x-2 bg-accent text-accent-foreground px-6 py-3 rounded-xl font-medium hover:bg-accent/90 transition-all duration-300 shadow-accent"
              >
                <span>Read Article</span>
                <motion.div
                  animate={{
                    x: isHovered ? 5 : 0,
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <Icon name="ArrowRight" size={16} />
                </motion.div>
              </Link>
            </div>
          </div>
        </div>

        {/* 3D Decorative Elements */}
        <motion.div
          className="absolute top-4 right-4 w-3 h-3 bg-accent/30 rounded-full"
          animate={{
            scale: isHovered ? [1, 1.5, 1] : 1,
            opacity: isHovered ? [0.3, 0.8, 0.3] : 0.3,
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        
        <motion.div
          className="absolute bottom-4 left-4 w-2 h-2 bg-success/30 rounded-full"
          animate={{
            scale: isHovered ? [1, 1.3, 1] : 1,
            opacity: isHovered ? [0.3, 0.6, 0.3] : 0.3,
          }}
          transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
        />
      </motion.article>
    </motion.section>
  );
};

export default FeaturedArticle;